<template>
  <el-row
    class="w-full h-full items-center justify-center overflow-auto p-6"
    :data-testid="dataTestId"
  >
    <el-col :xs="12" :span="8">
      <div
        v-if="computedIcon"
        class="flex items-center justify-center w-10 h-10 text-th-text-regular bg-th-lunar-gray rounded-md"
      >
        <el-icon class="text-2xl" aria-hidden="true">
          <component :is="computedIcon" />
        </el-icon>
      </div>

      <strong class="text-xl block mt-4 mb-2 uz:mb-1">{{ customTitle }}</strong>

      <div class="text-th-text-regular text-base">
        <slot name="description">
          <p class="m-0">
            {{ customDescription }}
          </p>
        </slot>
      </div>

      <!-- actions buttons -->
      <div v-if="$slots.actions" class="actions mt-4 flex items-center gap-2">
        <slot name="actions" />
      </div>

      <div v-if="learnMorePath" class="mt-4">
        <router-link
          :to="learnMorePath"
          class="inline-flex items-center text-th-primary"
        >
          <span>{{ customLearnMoreText }}</span>
          <arrow-right-icon class="ml-1" aria-hidden="true" />
        </router-link>
      </div>
    </el-col>
  </el-row>
</template>

<script setup>
import { computed } from 'vue'
import { useAppConfigStore } from '@shared/store/app-config'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ArrowRightIcon } from '@shared/assets/icons'

const { t } = useI18n()
const route = useRoute()
const appConfigStore = useAppConfigStore()
const { navigation } = storeToRefs(appConfigStore)

const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  description: {
    type: String,
    default: ''
  },
  learnMorePath: {
    type: Object,
    default: undefined
  },
  learnMoreText: {
    type: String,
    default: ''
  },
  icon: {
    type: [String, Object],
    default: undefined
  },
  dataTestId: {
    type: String,
    default: 'empty-view'
  }
})

const navItem = computed(() => {
  const currentRoute = route.path.endsWith('/')
    ? route.path.slice(0, -1)
    : route.path

  return findNavigationItem(currentRoute, navigation.value)
})

const customTitle = computed(() => props.title || t(navItem.value.key))

const customDescription = computed(() => {
  return props.description || t('common.data.no_data')
})

const customLearnMoreText = computed(() => {
  return (
    props.learnMoreText ||
    t('components.empty.learn', { entity: t(navItem.value.key) })
  )
})

const computedIcon = computed(() => props.icon || navItem.value.icon)

/**
 * Finds the navigation item that matches the current route.
 * It checks both top-level and child routes to find the best match.
 *
 * @param {string} currentRoute - The current route path.
 * @param {Array} navigationItems - The list of navigation items to search through.
 * @returns {Object} - The matching navigation item or a default object if none found.
 */
const findNavigationItem = (currentRoute, navigationItems) => {
  // Find the current navigation item based on the route
  const currentPath = navigationItems.find((path) => {
    if (currentRoute === path.route) return true

    if (currentRoute.startsWith(`/${path.name}`)) {
      if (!path.children) return true
      const matchingChild = path.children.find((child) => {
        return currentRoute === `${path.route + child.route}`
      })
      if (matchingChild) return true
    }

    return false
  })

  // Return null if no matching path found (should not happen)
  if (!currentPath) return { key: null, icon: null }

  // If the current path has no children or the route matches exactly, return it
  if (currentRoute === currentPath.route || !currentPath.children) {
    return currentPath
  }

  const matchingChild = currentPath.children.find(
    (child) => currentRoute === currentPath.route + child.route
  )

  // Return the matching child with the parent's icon, or the parent if no child matches
  return matchingChild
    ? { ...matchingChild, icon: currentPath.icon }
    : currentPath
}
</script>

<style scoped>
.actions :deep(.el-button + .el-button) {
  margin-left: 0;
}
</style>
